import { useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useParams } from 'react-router-dom';

import LinearProgress from '@mui/material/LinearProgress';

import { Board } from '@/models/BoardModel.ts';

import {
  selectActiveBoard,
  selectIsLoadingActiveBoard,
  selectIsLoadingRoomMembers,
  selectRoomMembersRequested,
} from '../../ducks/Boards';
import { actions as communityActions } from '../../ducks/Communities';
import { selectDeliveriesRequested, selectIsLoadingDeliveries } from '../../ducks/Deliveries';
import { actions as roomActions } from '../../ducks/Rooms';
import { alertAction } from '../../thunks/Alerts';
import * as boardsActions from '../../thunks/Boards';
import * as deliveriesActions from '../../thunks/Deliveries';

import Error from '../../common/Error';
import { DeliveryCreateContainer as DeliveryCreate, DeliveryUpdateContainer as DeliveryUpdate } from './';

const mapStateToProps = () =>
  createStructuredSelector({
    board: selectActiveBoard(),
    isLoadingBoard: selectIsLoadingActiveBoard(),
    isLoadingDeliveries: selectIsLoadingDeliveries(),
    deliveriesRequested: selectDeliveriesRequested(),
    roomMembersRequested: selectRoomMembersRequested(),
    isLoadingRoomMembers: selectIsLoadingRoomMembers(),
  });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators(
    {
      ...deliveriesActions,
      ...boardsActions,
      setActiveRoom: roomActions.setActiveRoom,
      setActiveCommunity: communityActions.setActiveCommunity,
    },
    dispatch,
  ),
});

const DeliveryContainer = ({
  board,
  isLoadingBoard,
  isLoadingDeliveries,
  deliveriesRequested,
  roomMembersRequested,
  isLoadingRoomMembers,
  actions,
}: {
  board: Board;
  isLoadingBoard: boolean;
  isLoadingDeliveries: boolean;
  deliveriesRequested: { boardId: string };
  roomMembersRequested: { communityId: string; roomId: string };
  isLoadingRoomMembers: boolean;
  actions: any;
}) => {
  const params = useParams();
  const { communityId, roomId, boardId, deliveryId } = params;

  useEffect(() => {
    if (!board || board.id !== boardId) {
      actions.fetchActiveBoard({ communityId, roomId, boardId }).then((action: any) => {
        const workflowConfig = action.payload.activeBoard.workflow_config;

        actions.setActiveCommunity({ communityId: workflowConfig.community_id });
        actions.setActiveRoom({ roomId: workflowConfig.room_id });

        alertAction({ action, error: intl.get('card.fetch_board.error') });
      });
    }

    if (deliveriesRequested.boardId !== boardId) {
      actions
        .fetchDeliveries({ communityId, roomId, boardId })
        .then((action: any) => alertAction({ action, error: intl.get('delivery.fetch.error') }));
    }

    if (roomMembersRequested.communityId !== communityId || roomMembersRequested.roomId !== roomId) {
      actions.fetchRoomMembers({ communityId, roomId, boardId });
    }
  }, [communityId, roomId, boardId, actions]);

  if (isLoadingBoard) {
    return <LinearProgress />;
  }

  if (!board) {
    return <Error text={intl.get('board.not_found')} />;
  }

  if (
    board?.id !== boardId ||
    isLoadingDeliveries ||
    !deliveriesRequested ||
    isLoadingRoomMembers ||
    roomMembersRequested.communityId !== communityId ||
    roomMembersRequested.roomId !== roomId
  ) {
    return <LinearProgress />;
  }

  if (deliveryId) {
    return <DeliveryUpdate params={params} />;
  }

  return <DeliveryCreate />;
};

const ConnectedDeliveryContainer = connect(mapStateToProps, mapDispatchToProps)(DeliveryContainer);

export default ConnectedDeliveryContainer;
